import React from 'react';
import { Link } from 'react-router-dom';
import { isAbsoluteUrl } from 'modules/core/utils/url';

export interface RoutedAnchorProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** render a plain anchor even for an in-app path (e.g. a path served by the backend rather than the SPA, or one that may be a download) */
  forceNoRouter?: boolean;
}

/**
 * An unstyled anchor that keeps in-app navigation client-side. Relative paths render as a router `Link`;
 * absolute URLs, downloads, `forceNoRouter`, and href-less (disabled) anchors render natively.
 * `Link` already leaves new-tab targets and modified clicks (cmd/ctrl/middle) to the browser.
 *
 * Use it as the `as` target for Canvas components that bring their own styling (e.g. `Menu.Item.as(RoutedAnchor)`).
 * For a visible text link, use `DevsiteLink`.
 */
export const RoutedAnchor = React.forwardRef<HTMLAnchorElement, RoutedAnchorProps>(
  ({ href, forceNoRouter, children, ...elemProps }, ref) => {
    if (!href || forceNoRouter || elemProps.download || isAbsoluteUrl(href)) {
      return (
        <a {...elemProps} href={href} ref={ref}>
          {children}
        </a>
      );
    }
    return (
      <Link {...elemProps} to={href} ref={ref}>
        {children}
      </Link>
    );
  }
);
RoutedAnchor.displayName = 'RoutedAnchor';
